import { FEATURE_QUERY_PARAM } from './feature.constants'
import { resolveClientFeature } from './resolveClientFeature'

/**
 * Patches the global `fetch` to append `?feature=<value>` to every
 * same-origin request that is missing the param.
 *
 * The K8s ingress routes by `?feature=X`. Any request that doesn't carry it
 * (Next.js RSC payloads, static chunks, prefetches, navigations triggered
 * from <Link>, calls from HTTPClient/axios, etc.) lands on the default pod
 * and breaks.
 *
 * The Service Worker (feature-sw.js) does the same thing at the network
 * layer; this interceptor is a JS-side safety net for the in-page lifetime,
 * also covering the brief window between page load and SW activation.
 *
 * Call once from a client component mounted early in the tree (e.g. layout).
 * Safe to call multiple times — subsequent calls are no-ops.
 */

let interceptorInstalled = false

export function installFeatureFetchInterceptor(): void {
  if (typeof window === 'undefined') return
  if (interceptorInstalled) return
  interceptorInstalled = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = function featureAwareFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const feature = resolveClientFeature()

    if (!feature) {
      return originalFetch(input, init)
    }

    const url = new URL(
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url,
      window.location.origin,
    )

    // Only modify same-origin requests.
    if (url.origin !== window.location.origin) {
      return originalFetch(input, init)
    }

    // Skip the backend proxy (vite.config.ts → /api-be/) — the proxy forwards
    // the query string verbatim to the backend, which may reject unknown
    // params. The K8s ingress routes /api-be/* by host/cookie/header, not by
    // the feature query.
    if (url.pathname.startsWith('/api-be/')) {
      return originalFetch(input, init)
    }

    // Already carries the param — leave it alone.
    if (url.searchParams.has(FEATURE_QUERY_PARAM)) {
      return originalFetch(input, init)
    }

    url.searchParams.set(FEATURE_QUERY_PARAM, feature)

    const modifiedInput =
      input instanceof Request
        ? new Request(url.toString(), input)
        : url.toString()

    return originalFetch(modifiedInput, init)
  }
}
